<template>

    <!-- Skeleton del podio durante il primo caricamento -->
    <div v-if="loading" class="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-end sm:gap-6">
        <div
            v-for="config in skeletonOrder"
            :key="config.place"
            class="flex flex-col"
            :class="config.order"
        >
            <div class="animate-pulse rounded-2xl bg-surface-200 dark:bg-surface-800 h-20" :class="config.height"></div>
        </div>
    </div>

    <!-- Podio -->
    <ol v-else-if="podium.length > 0" class="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-end sm:gap-6">

        <li
            v-for="team in podium"
            :key="team.posizione"
            class="flex flex-col"
            :class="styles[team.place].order"
        >

            <!-- Card della squadra: riga compatta su mobile, colonna da sm in su -->
            <div
                class="relative overflow-hidden rounded-2xl px-4 py-4 ring-1 bg-white dark:bg-surface-900 shadow-lg shadow-black/[0.03] sm:rounded-b-none sm:pb-6"
                :class="[styles[team.place].card, team.place === 1 ? 'sm:pt-8' : 'sm:pt-6']"
                :aria-label="`${team.place}° posto: ${team.squadra}, ${formatPoints(team.punti_totali)} punti`"
            >

                <!-- Velatura colorata, richiama il metallo della posizione -->
                <div class="pointer-events-none absolute inset-x-0 top-0 h-full bg-gradient-to-r sm:h-24 sm:bg-gradient-to-b" :class="styles[team.place].wash"></div>

                <div class="relative flex items-center gap-4 text-left sm:block sm:text-center">

                    <!-- Medaglia -->
                    <div class="relative w-fit shrink-0 sm:mx-auto sm:mb-4">

                        <!-- Anello rotante, riservato al primo posto (stesso effetto della sezione "Usiamo l'app di Fantagazetta") -->
                        <div
                            v-if="team.place === 1"
                            class="absolute -inset-2 rounded-full animate-[spin_4s_linear_infinite] border border-transparent [background:conic-gradient(from_180deg,transparent,theme(colors.primary))_border-box] [mask-composite:exclude_!important] [mask:linear-gradient(white_0_0)_padding-box,_linear-gradient(white_0_0)]"
                        ></div>

                        <div
                            class="relative flex items-center justify-center rounded-full"
                            :class="[styles[team.place].medal, team.place === 1 ? 'h-12 w-12 sm:h-16 sm:w-16' : 'h-11 w-11 sm:h-12 sm:w-12']"
                        >
                            <span
                                class="material-symbols-outlined material-symbols-fill-1"
                                :class="team.place === 1 ? '!text-2xl sm:!text-3xl' : '!text-xl sm:!text-2xl'"
                            >
                                {{ styles[team.place].icon }}
                            </span>
                        </div>

                    </div>

                    <!-- Posizione e nome della squadra -->
                    <div class="min-w-0 flex-1 sm:flex-none">

                        <p class="text-[0.7rem] font-bold uppercase tracking-[0.2em]" :class="styles[team.place].label">
                            {{ styles[team.place].title }}
                        </p>

                        <h3
                            class="mt-0.5 break-words font-bold leading-tight sm:mt-1.5"
                            :class="team.place === 1 ? 'text-lg sm:text-xl' : 'text-base sm:text-lg'"
                        >
                            {{ team.squadra }}
                        </h3>

                    </div>

                    <!-- Punti e distacco -->
                    <div class="shrink-0 text-right sm:text-center">

                        <p
                            class="font-black tabular-nums leading-none sm:mt-4"
                            :class="team.place === 1 ? 'text-2xl sm:text-4xl' : 'text-xl sm:text-3xl'"
                        >
                            {{ formatPoints(team.punti_totali) }}
                        </p>

                        <p class="mt-1 text-[0.7rem] uppercase tracking-[0.2em] text-surface-500 dark:text-surface-400">
                            punti
                        </p>

                        <!-- Distacco dalla prima posizione, oppure etichetta di testa per il leader -->
                        <p
                            v-if="team.gap"
                            class="mt-2 inline-flex items-center gap-1 rounded-full bg-surface-100 px-2.5 py-1 text-xs font-semibold tabular-nums text-surface-600 dark:bg-surface-800 dark:text-surface-300 sm:mt-3"
                        >
                            <span class="material-symbols-outlined !text-sm">trending_down</span>
                            {{ team.gap }} dal 1°
                        </p>

                        <p
                            v-else
                            class="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/25 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-amber-800 dark:bg-primary/20 dark:text-primary sm:mt-3"
                        >
                            <span class="material-symbols-outlined material-symbols-fill-1 !text-sm">star</span>
                            In testa
                        </p>

                    </div>

                </div>

            </div>

            <!-- Gradino del podio: su mobile la posizione è già chiara dall'ordine delle card -->
            <div
                class="hidden items-center justify-center rounded-b-2xl text-2xl font-black text-secondary/70 shadow-lg shadow-black/[0.03] sm:flex"
                :class="styles[team.place].step"
            >
                {{ team.place }}
            </div>

        </li>

    </ol>

</template>

<script setup>

    // based imports
    import { computed } from 'vue';

    // services imports
    import { formatPoints, formatGap } from '@/helpers/number';

    // definisco le props
    const props = defineProps({
        standings: {
            type: Array,
            default: () => []
            // Le prime tre squadre della classifica: [{ posizione, squadra, punti_totali }]
        },
        loading: {
            type: Boolean,
            default: false
        }
    });

    /*
        Stili per posizione: oro (colore del brand), argento e bronzo.
        `order` gestisce la disposizione a podio 2 - 1 - 3 da sm in su,
        mentre l'ordine del DOM resta 1 - 2 - 3 per screen reader e tastiera.
    */
    const styles = {
        1: {
            title: 'Campione',
            icon: 'emoji_events',
            order: 'order-1 sm:order-2',
            card: 'ring-primary/70 dark:ring-primary/50',
            wash: 'from-primary/25 to-transparent',
            medal: 'bg-primary text-secondary shadow-lg shadow-primary/40',
            label: 'text-amber-600 dark:text-primary',
            step: 'h-16 sm:h-28 bg-gradient-to-b from-primary to-primary/70'
        },
        2: {
            title: 'Secondo posto',
            icon: 'military_tech',
            order: 'order-2 sm:order-1',
            card: 'ring-zinc-300 dark:ring-zinc-600',
            wash: 'from-zinc-300/30 to-transparent',
            medal: 'bg-zinc-300 text-zinc-700 shadow-lg shadow-zinc-400/30',
            label: 'text-zinc-500 dark:text-zinc-300',
            step: 'h-14 sm:h-16 bg-gradient-to-b from-zinc-300 to-zinc-300/70'
        },
        3: {
            title: 'Terzo posto',
            icon: 'military_tech',
            order: 'order-3 sm:order-3',
            card: 'ring-amber-600/50 dark:ring-amber-700/60',
            wash: 'from-amber-600/15 to-transparent',
            medal: 'bg-amber-600 text-white shadow-lg shadow-amber-700/30',
            label: 'text-amber-700 dark:text-amber-500',
            step: 'h-12 sm:h-10 bg-gradient-to-b from-amber-600 to-amber-600/70'
        }
    };

    // altezze e ordine degli skeleton, così il podio non "salta" al termine del caricamento
    const skeletonOrder = [
        { place: 1, order: 'order-1 sm:order-2', height: 'sm:h-[21.5rem]' },
        { place: 2, order: 'order-2 sm:order-1', height: 'sm:h-[19rem]' },
        { place: 3, order: 'order-3 sm:order-3', height: 'sm:h-[18rem]' }
    ];

    /*
        Normalizzo le prime tre squadre aggiungendo il posto sul podio e il distacco
        dalla prima. Uso l'indice e non `posizione`, così il podio regge anche
        eventuali pari merito o numerazioni non consecutive lato backend.
    */
    const podium = computed(() => {

        if (!Array.isArray(props.standings) || props.standings.length === 0) {
            return [];
        }

        const leaderPoints = parseFloat(props.standings[0]?.punti_totali);

        return props.standings.slice(0, 3).map((team, index) => {

            const points = parseFloat(team?.punti_totali);
            const gap = Number.isFinite(points) && Number.isFinite(leaderPoints) ? points - leaderPoints : null;

            return {
                ...team,
                place: index + 1,
                gap: formatGap(gap)
            };
        });
    });

</script>
